import {
    AfterViewInit,
    Directive,
    ElementRef,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Renderer2,
    SimpleChanges
} from '@angular/core';

@Directive({
    selector: '[appFitText]',
    standalone: false
})
export class FitTextDirective implements AfterViewInit, OnInit, OnChanges {
    @Input() fittext? = true;
    @Input() compression = 1;
    activateOnResize = false;
    @Input() minFontSize: number | 'inherit' = "inherit";
    @Input() maxFontSize: number | 'inherit' = "inherit";
    @Input() delay = 100;
    @Input() innerHTML: string = '';
    @Input() fontUnit?: 'px' | 'em' | string = 'px';

    private fittextParent: HTMLElement;
    private fittextElement: HTMLElement;
    private fittextMinFontSize: number = NaN;
    private fittextMaxFontSize: number = NaN;
    private computed: CSSStyleDeclaration;
    private newlines: number;
    private lineHeight: string;
    private display: string;
    private calcSize = 10;
    private resizeTimeout: any;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2
    ) {
        this.fittextElement = el.nativeElement;
        this.fittextParent = this.fittextElement.parentElement!;
        this.computed = window.getComputedStyle(this.fittextElement);
        this.newlines = this.fittextElement.childElementCount > 0 ? this.fittextElement.childElementCount : 1;
        this.lineHeight = this.computed.lineHeight;
        this.display = this.computed['display'];
    }

    @HostListener('window:resize')
    public onWindowResize = (): void => {
        if (this.activateOnResize) {
            this.setFontSize();
        }
    };

    getOnlyNumberInString(value: string): number {
        let i = value.length;
        let lastIndex;
        while (i > 0) {
            const parsedValue = parseInt(value.charAt(i));
            if (!isNaN(parsedValue) && typeof parsedValue == "number") {
                lastIndex = i + 1;
            }
            i--
        }
        const onlyNumber = value.substring(0, lastIndex)
        return parseFloat(onlyNumber)
    }

    public ngOnInit() {
        this.activateOnResize = true
        const fontSizeNumber = this.getOnlyNumberInString(this.computed!.fontSize!)
        this.fittextMinFontSize = this.minFontSize === 'inherit' ? fontSizeNumber : this.minFontSize;
        this.fittextMaxFontSize = this.maxFontSize === 'inherit' ? fontSizeNumber : this.maxFontSize;
    }

    public ngAfterViewInit() {
        this.setFontSize(0);
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes['compression'] && !changes['compression'].firstChange) {
            this.setFontSize(0);
        }
        if (changes['innerHTML']) {
            this.fittextElement.innerHTML = this.innerHTML;
            if (!changes['innerHTML'].firstChange) {
                this.setFontSize(0);
            }
        }
    }

    private setFontSize = (delay: number = this.delay): void => {
        this.resizeTimeout = setTimeout(
            (() => {
                if (this.fittextElement.offsetHeight * this.fittextElement.offsetWidth !== 0) {
                    // reset to default
                    this.setStyles(this.calcSize, 1, 'inline-block');
                    // set new
                    this.setStyles(this.calculateNewFontSize(), this.lineHeight, this.display);
                }
            }).bind(this),
            delay
        );
    };

    private calculateNewFontSize = (): number => {

        let compressedText = this.fittextMaxFontSize;
        if (this.fittextParent) {
            const ratio = (this.calcSize * this.newlines) / this.fittextElement.offsetWidth / this.newlines;
            const offsetWidth = this.fittextParent != null ? this.fittextParent.offsetWidth : 0;
            const getComputedStyleFittexParent = window.getComputedStyle(this.fittextParent);
            const paddingLeft = getComputedStyleFittexParent != null ? parseFloat(getComputedStyleFittexParent.paddingLeft) : 0;
            const paddingRight = getComputedStyleFittexParent != null ? parseFloat(getComputedStyleFittexParent.paddingRight) : 0;
            compressedText = (offsetWidth - (paddingLeft + paddingRight) - 6) * ratio * this.compression;
        }
        return Math.max(
            Math.min(
                compressedText, this.fittextMaxFontSize
            ),
            this.fittextMinFontSize
        );
    };

    private setStyles = (fontSize: number, lineHeight: number | string, display: string): void => {
        this.renderer.setStyle(this.fittextElement, 'fontSize', fontSize.toString() + this.fontUnit);
        this.renderer.setStyle(this.fittextElement, 'lineHeight', lineHeight.toString());
        this.renderer.setStyle(this.fittextElement, 'display', display);
    };

}
